import {
  Heading,
  Tbody,
  Thead,
  Table,
  Text,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  Skeleton,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useState } from 'react';

import { Bank } from '@finley-technologies/finley-types';
import {
  generateReceivableStats,
  getDedupedTotalMarketValue,
  parse,
  orderLoanAccountsLoans,
  getAllocatedTotalLendableValue,
} from '@finley-technologies/valley-bank-utils';
import type {
  ValleyBankCustomerWithRelationships,
  ValleyBankLoanAccount,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankCustomerQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { Alert } from 'packages/common-frontend/components/Alert';
import { CsvXlsxDownloadSelect } from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from 'packages/common-frontend/components/icons';
import { InfoCard } from 'packages/common-frontend/components/InfoCard';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from 'packages/common-frontend/utils';
import {
  CollateralAccountArrangementGroupNumbers,
  getArrangementGroupNumbers,
} from 'valleyBank/domains/Collateral/ui/CollateralAccountArrangementGroupNumbers';
import { CollateralAccountLoanAccountNumbers } from 'valleyBank/domains/Collateral/ui/CollateralAccountLoanAccountNumbers';
import {
  CustomerSummaryLink,
  useCustomerUrlParam,
} from 'valleyBank/domains/Customers/hooks/CustomerContext';
import { CustomerNotFoundAlert } from 'valleyBank/domains/Customers/ui/details/CustomerNotFoundAlert';
import { LoansTable } from 'valleyBank/domains/LoanAccounts/ui/LoansTable';
import { useDataMetadataContext } from 'valleyBank/domains/page/hooks/DataMetadataContext';
import { LoanAndCollateralAccountsStats } from 'valleyBank/domains/page/LoanAccountsAndCollateralAccountsStats';
import { BoundingBox } from 'valleyBank/domains/page/ui/BoundingBox';

const collateralAccountsDataKeyToHeaderMap = {
  accountNumber: 'Account Number',
  accountName: 'Account Name',
  productName: 'Product Name',
  loanAccountNumbers: 'Loan Accounts',
  arrangementGroupNumbers: 'Arrangement Group',
  collateralType: 'Collateral Type',
  cifNum: 'CIF Number',
  marketValue: 'Market Value',
  effectiveAdvanceRate: 'Blended Advance Rate',
  preAllocationLendableValue: 'Lendable Value',
};

const collateralAccountsHeaders = _.map(
  collateralAccountsDataKeyToHeaderMap,
  (header, key) => ({ label: header, key }),
);

export const SummaryOverviewStatus: React.FC<{}> = () => {
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const [
    customer,
    setCustomer,
  ] = useState<ValleyBankCustomerWithRelationships | null>(null);
  const [arrangmentGroup, setArrangementGroup] = useState<string | null>(null);

  // @jendevelops TODO: replace with loanParties props
  const { cifnum } = useCustomerUrlParam();
  const { loading: isLoading } = useGetValleyBankCustomerQuery({
    variables: {
      cifNumber: cifnum || '',
      reportingDate,
      isPreview,
    },
    onCompleted: (res) => {
      const customerJSON = res.valleyBankCustomer?.customer;
      if (customerJSON) {
        try {
          const newCustomer = parse.parseValleyBankCustomer(customerJSON);
          if (newCustomer) {
            const arrangementGroups = getArrangementGroupNumbers(
              newCustomer.collateralAccounts.flatMap(
                ({ receivables }) => receivables,
              ),
            );
            setArrangementGroup(arrangementGroups[0] || null);
            setAsOfDate(newCustomer.asOfDate);
            setCustomer({ ...newCustomer });
          }
        } catch (err) {
          Sentry.captureException(err);
          throw new Error('Failed to parse customer');
        }
      }
    },
  });

  if (isLoading) {
    return <Skeleton w="100%" h="600px" />;
  }

  const loanAccountLoanPartyMap = customer?.loanAccounts.reduce(
    (acc, loanAccount) => {
      return {
        ...acc,
        [loanAccount.loanAccountNumber]: _.keyBy(
          loanAccount.loanParties,
          'cif_num',
        ),
      };
    },
    {} as {
      [loanAccountNumber: string]: {
        [cifNumber: string]: ValleyBankLoanAccount['loanParties'][number];
      };
    },
  );

  const loans = orderLoanAccountsLoans(customer?.loanAccounts || []);

  const collateralAccounts = customer?.collateralAccounts || [];

  const receivables =
    customer?.collateralAccounts.flatMap(
      ({ receivables: _receivables }) => _receivables,
    ) || [];

  const [assignedReceivables, unassignedReceivables] = _.partition(
    receivables,
    (receivable) => Boolean(receivable.loan_account_number),
  );

  const collateralAccountsTableData = _.map(collateralAccounts, (account) => {
    const {
      accountNumber,
      marketValue,
      effectiveAdvanceRate,
      preAllocationLendableValue,
      productName,
      receivables: collateralAccountReceivables,
    } = account;

    const loanAccountNumbersStr =
      collateralAccountReceivables[0]?.receivableReponseTapeRow.baseFields
        .finley_assigned_to_loan_accounts;

    const loanAccountNumbers =
      typeof loanAccountNumbersStr === 'string'
        ? loanAccountNumbersStr
        : 'unassigned';

    const arrangementGroupNumbersStr = getArrangementGroupNumbers(
      collateralAccountReceivables,
    );
    const arrangementGroupNumbers =
      arrangementGroupNumbersStr.length === 0 && arrangmentGroup
        ? arrangmentGroup
        : arrangementGroupNumbersStr.join('|');

    return {
      accountNumber,
      accountName: '', // todo: add account name
      productName,
      loanAccountNumbers,
      arrangementGroupNumbers,
      collateralType: '', // todo: add collateral type
      cifNum: cifnum,
      marketValue,
      effectiveAdvanceRate,
      preAllocationLendableValue,
    };
  });

  const {
    totalPreAllocationLendableValue: unassignedTotalPreAllocationLendableValue,
    totalMarketValue: unassignedTotalMarketValue,
  } = generateReceivableStats(unassignedReceivables);

  return (
    <>
      {!isLoading && !customer && <CustomerNotFoundAlert />}

      {customer && (
        <VStack w="100%" spacing="24px" alignItems="flex-start">
          <LoanAndCollateralAccountsStats
            collateralStats={{
              assigned: generateReceivableStats(assignedReceivables),
              unassigned: generateReceivableStats(unassignedReceivables),
            }}
          />

          <BoundingBox>
            <LoansTable
              loans={loans}
              noLoansMessage="There are no loan associated with this customer."
              title="Loans"
              loanAccountLoanPartyMap={loanAccountLoanPartyMap}
              cifNumber={cifnum}
            />
          </BoundingBox>

          <BoundingBox>
            <Heading as="h2" fontSize="24px" mb="24px">
              Collateral Accounts
            </Heading>

            {collateralAccounts.length === 0 && (
              <Alert status="info">
                <Text>
                  No Assigned Collateral associated with this Customer
                </Text>
              </Alert>
            )}
            {collateralAccounts.length > 0 && (
              <>
                <HStack
                  spacing="24px"
                  w="100%"
                  style={{ marginBottom: '24px' }}
                >
                  <InfoCard
                    title="Total Assigned Market Value"
                    value={formatCurrencyAmount(
                      getDedupedTotalMarketValue(assignedReceivables),
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                  <InfoCard
                    title="Total Assigned Lendable Value"
                    value={formatCurrencyAmount(
                      getAllocatedTotalLendableValue(assignedReceivables),
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                  <InfoCard
                    title="Total Unassigned Market Value"
                    value={formatCurrencyAmount(
                      unassignedTotalMarketValue,
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                  <InfoCard
                    title="Total Unassigned Lendable Value"
                    value={formatCurrencyAmount(
                      unassignedTotalPreAllocationLendableValue,
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                </HStack>
                {/* @jendevelops TODO implement search and show/hide columns */}
                {/* <HStack w="100%" spacing="16px" justifyContent="flex-end">
              <Button variant="outline" colorScheme="aurora">
                Show/Hide Columns
              </Button>
              <Button
                variant="outline"
                colorScheme="aurora"
                leftIcon={<Icon icon="search" />}
              >
                Search
              </Button>
            </HStack> */}
                <CsvXlsxDownloadSelect
                  dataRows={collateralAccountsTableData}
                  headers={collateralAccountsHeaders}
                  filenamePrefix={`collateral-accounts-cif-${cifnum}`}
                />
                <Table>
                  <Thead>
                    <Tr>
                      {_.map(collateralAccountsDataKeyToHeaderMap, (header) => (
                        <Th>{header}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {collateralAccounts.map(
                      ({
                        accountNumber,
                        productName,
                        marketValue,
                        effectiveAdvanceRate,
                        preAllocationLendableValue,
                        receivables: collateralAccountReceivables,
                      }) => {
                        return (
                          <Tr key={accountNumber}>
                            <Td>{accountNumber}</Td>
                            {/* @jendevelops TODO: add account name */}
                            <Td>-</Td>
                            <Td>{productName}</Td>
                            {/* @jendevelops is this supposed to be a list of loan
                        account numbers, since loanAccount is receivable level can be different across the collateral account. */}
                            <Td>
                              <CollateralAccountLoanAccountNumbers
                                receivables={collateralAccountReceivables}
                              />
                            </Td>
                            {/* @jendevelops is this supposed to be a list of arrangement group numbers, since arrangement group is receivable level can be different across the collateral account. */}
                            <Td>
                              <CollateralAccountArrangementGroupNumbers
                                receivables={collateralAccountReceivables}
                                arrangementGroupOverride={arrangmentGroup}
                              />
                            </Td>
                            {/* TODO add collateral type */}
                            <Td>-</Td>
                            <Td>
                              <CustomerSummaryLink cifnum={cifnum || ''}>
                                {cifnum}
                              </CustomerSummaryLink>
                            </Td>
                            <Td>
                              {formatCurrencyAmount(
                                marketValue,
                                Bank.Currency.USD,
                              )}
                            </Td>
                            <Td>
                              {percentageToPercentageLabel(
                                effectiveAdvanceRate * 100,
                              )}
                            </Td>
                            <Td>
                              {formatCurrencyAmount(
                                preAllocationLendableValue,
                                Bank.Currency.USD,
                              )}
                            </Td>
                          </Tr>
                        );
                      },
                    )}
                  </Tbody>
                </Table>
              </>
            )}
          </BoundingBox>
        </VStack>
      )}
    </>
  );
};
